import { useCallback, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

const asList = (data) => (Array.isArray(data) ? data : data?.results || []);
const idempotencyKey = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const usePayrollRuns = () => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [inputs, setInputs] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [components, setComponents] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.finance.payrollPeriods,
        null,
        navigate,
      );
      if (response.ok) setPeriods(asList(response.data));
      else Utilities.showError(response, "Failed to load payroll periods");
    } catch (error) {
      console.error(error);
      message.error("Failed to load payroll periods");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchInputOptions = useCallback(async () => {
    try {
      const [profilesResponse, componentsResponse, accountsResponse] =
        await Promise.all([
          Utilities.fetchWithAuth(
            "GET",
            domainSettings.urls.finance.payrollProfiles,
            null,
            navigate,
          ),
          Utilities.fetchWithAuth(
            "GET",
            domainSettings.urls.finance.payrollComponents,
            null,
            navigate,
          ),
          Utilities.fetchWithAuth(
            "GET",
            domainSettings.urls.finance.payrollPaymentAccounts,
            null,
            navigate,
          ),
        ]);
      if (profilesResponse.ok) setProfiles(asList(profilesResponse.data));
      else
        Utilities.showError(
          profilesResponse,
          "Failed to load payroll employees",
        );
      if (componentsResponse.ok) setComponents(asList(componentsResponse.data));
      else
        Utilities.showError(
          componentsResponse,
          "Failed to load payroll components",
        );
      if (accountsResponse.ok)
        setPaymentAccounts(asList(accountsResponse.data));
      else
        Utilities.showError(
          accountsResponse,
          "Failed to load school payment accounts",
        );
    } catch (error) {
      console.error(error);
      message.error("Failed to load monthly input options");
    }
  }, [navigate]);

  const fetchInputs = useCallback(
    async (periodId) => {
      setLoading(true);
      try {
        const url = `${domainSettings.urls.finance.payrollInputs}?period_id=${periodId}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) setInputs(asList(response.data));
        else
          Utilities.showError(
            response,
            "Failed to load monthly payroll inputs",
          );
      } catch (error) {
        console.error(error);
        message.error("Failed to load monthly payroll inputs");
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const saveInput = useCallback(
    async (inputId, data) => {
      setSaving(true);
      try {
        const url = inputId
          ? domainSettings.urls.finance.payrollInputDetail.replace(
              ":id",
              inputId,
            )
          : domainSettings.urls.finance.payrollInputs;
        const response = await Utilities.fetchWithAuth(
          inputId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not save the monthly payroll input",
          );
          return null;
        }
        message.success(
          inputId ? "Monthly input updated" : "Monthly input added",
        );
        await Promise.all([fetchInputs(data.periodId), fetchPeriods()]);
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not save the monthly payroll input");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchInputs, fetchPeriods, navigate],
  );

  const deleteInput = useCallback(
    async (inputId, periodId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollInputDetail.replace(
          ":id",
          inputId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not delete the monthly payroll input",
          );
          return false;
        }
        message.success("Monthly input deleted");
        await Promise.all([fetchInputs(periodId), fetchPeriods()]);
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the monthly payroll input");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [fetchInputs, fetchPeriods, navigate],
  );

  const savePeriod = useCallback(
    async (periodId, data) => {
      setSaving(true);
      try {
        const url = periodId
          ? domainSettings.urls.finance.payrollPeriodDetail.replace(
              ":id",
              periodId,
            )
          : domainSettings.urls.finance.payrollPeriods;
        const response = await Utilities.fetchWithAuth(
          periodId ? "PATCH" : "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not save the payroll period");
          return null;
        }
        message.success(
          periodId ? "Payroll period updated" : "Payroll period created",
        );
        await fetchPeriods();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not save the payroll period");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const generateDraft = useCallback(
    async (periodId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollGenerateDraft.replace(
          ":id",
          periodId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { idempotencyKey: idempotencyKey() },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not generate the draft payroll");
          return null;
        }
        if (response.data?.backgroundJob) {
          message.success(
            "Payroll generation queued in the background. Follow it in Payroll Reports → Operations.",
          );
        } else {
          message.success("Draft payroll generated");
        }
        await fetchPeriods();
        return response.data?.backgroundJob ? null : response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not generate the draft payroll");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const deletePeriod = useCallback(
    async (periodId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollPeriodDetail.replace(
          ":id",
          periodId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not delete the payroll period");
          return false;
        }
        message.success("Payroll period deleted");
        await fetchPeriods();
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the payroll period");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const deleteRun = useCallback(
    async (runId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollRunDetail.replace(
          ":id",
          runId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not delete the draft payroll");
          return false;
        }
        message.success("Draft payroll deleted");
        await fetchPeriods();
        return true;
      } catch (error) {
        console.error(error);
        message.error("Could not delete the draft payroll");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const transitionRun = useCallback(
    async (runId, actionName, data = {}) => {
      const actions = {
        submit: {
          url: domainSettings.urls.finance.payrollRunSubmit,
          success: "Payroll submitted for review",
        },
        review: {
          url: domainSettings.urls.finance.payrollRunStartReview,
          success: "Payroll review started",
        },
        approve: {
          url: domainSettings.urls.finance.payrollRunApprove,
          success: "Payroll approved and locked",
        },
        reject: {
          url: domainSettings.urls.finance.payrollRunReject,
          success: "Payroll rejected",
        },
        returnToDraft: {
          url: domainSettings.urls.finance.payrollRunReturnToDraft,
          success: "Payroll returned to Draft",
        },
        finalize: {
          url: domainSettings.urls.finance.payrollRunFinalize,
          success: "Payroll finalized and payslips created",
        },
      };
      const workflowAction = actions[actionName];
      if (!workflowAction) return null;
      setSaving(true);
      try {
        const url = workflowAction.url.replace(":id", runId);
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { ...data, idempotencyKey: idempotencyKey() },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not update the payroll workflow",
          );
          return null;
        }
        message.success(workflowAction.success);
        await fetchPeriods();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not update the payroll workflow");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const fetchRun = useCallback(
    async (runId) => {
      const url = domainSettings.urls.finance.payrollRunDetail.replace(
        ":id",
        runId,
      );
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );
      if (!response.ok) {
        Utilities.showError(response, "Could not refresh the payroll");
        return null;
      }
      return response.data;
    },
    [navigate],
  );

  const openPayslipPdf = useCallback(
    async (url, filename, shouldPrint = false) => {
      const printWindow = shouldPrint ? window.open("", "_blank") : null;
      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${Utilities.getCookie("access")}` },
        });
        if (!response.ok) throw new Error("PDF request failed");
        const blobUrl = URL.createObjectURL(await response.blob());
        if (shouldPrint) {
          if (printWindow) printWindow.location = blobUrl;
          else window.open(blobUrl, "_blank");
        } else {
          const link = document.createElement("a");
          link.href = blobUrl;
          const disposition = response.headers.get("content-disposition") || "";
          link.download =
            disposition.match(/filename="?([^";]+)"?/i)?.[1] || filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } catch (error) {
        if (printWindow) printWindow.close();
        console.error(error);
        message.error("Could not open the payslip PDF");
      }
    },
    [],
  );

  const downloadPayslip = useCallback(
    (payslip, shouldPrint = false) => {
      const url = domainSettings.urls.finance.payrollPayslipPdf.replace(
        ":id",
        payslip.payslipId,
      );
      return openPayslipPdf(url, `${payslip.payslipNumber}.pdf`, shouldPrint);
    },
    [openPayslipPdf],
  );

  const downloadCombinedPayslips = useCallback(
    (run, shouldPrint = false) => {
      const url =
        domainSettings.urls.finance.payrollRunCombinedPayslips.replace(
          ":id",
          run.runId,
        );
      return openPayslipPdf(
        url,
        `Payroll_${run.periodName}_Payslips.pdf`,
        shouldPrint,
      );
    },
    [openPayslipPdf],
  );

  const downloadDraftPayslips = useCallback(
    (run, profileId = null) => {
      let url = domainSettings.urls.finance.payrollRunDraftPayslips.replace(
        ":id",
        run.runId,
      );
      if (profileId) url += `?profile_id=${profileId}`;
      return openPayslipPdf(url, `DRAFT_${run.periodName}_Payslips.pdf`, true);
    },
    [openPayslipPdf],
  );

  const downloadFile = useCallback(async (url, filename) => {
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${Utilities.getCookie("access")}` },
      });
      if (!response.ok) throw new Error("File request failed");
      const blobUrl = URL.createObjectURL(await response.blob());
      const disposition = response.headers.get("content-disposition") || "";
      const suppliedName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = suppliedName || filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      message.error("Could not download the payroll document");
    }
  }, []);

  const downloadPayslipsZip = useCallback(
    (run) => {
      const url = domainSettings.urls.finance.payrollRunPayslipsZip.replace(
        ":id",
        run.runId,
      );
      return downloadFile(url, `${run.periodName}_Payslips.zip`);
    },
    [downloadFile],
  );

  const downloadFinalSchedule = useCallback(
    (run, format) => {
      const url = `${domainSettings.urls.finance.payrollRunFinalSchedule.replace(":id", run.runId)}?export=${format}`;
      return downloadFile(url, `${run.periodName}_Final_Payroll.${format}`);
    },
    [downloadFile],
  );

  const emailPayslip = useCallback(
    async (runId, payslipId) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollPayslipEmail.replace(
          ":id",
          payslipId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {},
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not queue this payslip email");
          return null;
        }
        message.success("Payslip email queued");
        return fetchRun(runId);
      } catch (error) {
        console.error(error);
        message.error("Could not queue this payslip email");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchRun, navigate],
  );

  const emailAllPayslips = useCallback(
    async (runId, retryFailed = false) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollRunEmailPayslips.replace(
          ":id",
          runId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { retryFailed, idempotencyKey: idempotencyKey() },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not queue the payslip emails");
          return null;
        }
        message.success(`${response.data.queued} payslip email(s) queued`);
        if (response.data.skipped?.length) {
          message.warning(
            `${response.data.skipped.length} employee(s) were skipped; open email status for the reasons`,
          );
        }
        return fetchRun(runId);
      } catch (error) {
        console.error(error);
        message.error("Could not queue the payslip emails");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchRun, navigate],
  );

  const previewPaymentExport = useCallback(
    async (run) => {
      const url = domainSettings.urls.finance.payrollRunBankPreview.replace(
        ":id",
        run.runId,
      );
      const response = await Utilities.fetchWithAuth(
        "POST",
        url,
        {
          paymentStatus: run.status === "Paid" ? "All" : "Unpaid",
        },
        navigate,
      );
      if (!response.ok) {
        Utilities.showError(response, "Could not preview the export file");
        return null;
      }
      return response.data;
    },
    [navigate],
  );

  const downloadPaymentExport = useCallback(async (run) => {
    let url = domainSettings.urls.finance.payrollRunBankSchedule.replace(
      ":id",
      run.runId,
    );
    const paymentStatus = run.status === "Paid" ? "All" : "Unpaid";
    url += `?payment_status=${paymentStatus}`;
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${Utilities.getCookie("access")}` },
      });
      if (!response.ok) {
        let detail = "Could not download the export file";
        try {
          const error = await response.json();
          detail = Array.isArray(error.detail)
            ? error.detail.join(" ")
            : error.detail || detail;
        } catch (_) {
          // Keep the generic message when the server did not return JSON.
        }
        message.error(detail);
        return;
      }
      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      const disposition = response.headers.get("content-disposition") || "";
      const suppliedName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      link.download = suppliedName || `Bank_Payment_${run.periodEnd}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      message.error("Could not download the export file");
    }
  }, []);

  const recordPayment = useCallback(
    async (paymentId, data) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollPaymentRecord.replace(
          ":id",
          paymentId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { ...data, idempotencyKey: idempotencyKey() },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(
            response,
            "Could not record this employee payment",
          );
          return null;
        }
        message.success("Employee payment recorded");
        await fetchPeriods();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not record this employee payment");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const markPayrollPaid = useCallback(
    async (runId, data) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollRunMarkPaid.replace(
          ":id",
          runId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { ...data, idempotencyKey: idempotencyKey() },
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not mark the payroll Paid");
          return null;
        }
        message.success(
          `${response.data.markedPaidCount} employee payment(s) recorded`,
        );
        await fetchPeriods();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not mark the payroll Paid");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const reversePayroll = useCallback(
    async (runId, data) => {
      setSaving(true);
      try {
        const url = domainSettings.urls.finance.payrollRunReverse.replace(
          ":id",
          runId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          data,
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not reverse this payroll");
          return null;
        }
        message.success("Payroll reversed and correction period created");
        await fetchPeriods();
        return response.data;
      } catch (error) {
        console.error(error);
        message.error("Could not reverse this payroll");
        return null;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const previewAccounting = useCallback(
    async (runId) => {
      const url =
        domainSettings.urls.finance.payrollRunAccountingPreview.replace(
          ":id",
          runId,
        );
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );
      if (!response.ok) {
        Utilities.showError(
          response,
          "Could not prepare the accounting preview",
        );
        return null;
      }
      return response.data;
    },
    [navigate],
  );

  const postAccounting = useCallback(
    async (runId) => {
      setSaving(true);
      try {
        const url =
          domainSettings.urls.finance.payrollRunPostAccounting.replace(
            ":id",
            runId,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {},
          navigate,
        );
        if (!response.ok) {
          Utilities.showError(response, "Could not post the payroll journal");
          return null;
        }
        message.success(
          response.data.created
            ? "Payroll journal posted"
            : "This payroll was already posted",
        );
        await fetchPeriods();
        return response.data;
      } finally {
        setSaving(false);
      }
    },
    [fetchPeriods, navigate],
  );

  const fetchAccountingJournals = useCallback(
    async (runId) => {
      const url =
        domainSettings.urls.finance.payrollRunAccountingJournals.replace(
          ":id",
          runId,
        );
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );
      if (!response.ok) {
        Utilities.showError(response, "Could not load the accounting journals");
        return [];
      }
      return asList(response.data);
    },
    [navigate],
  );

  return {
    periods,
    inputs,
    profiles,
    components,
    paymentAccounts,
    loading,
    saving,
    fetchPeriods,
    fetchInputs,
    fetchInputOptions,
    saveInput,
    deleteInput,
    savePeriod,
    generateDraft,
    deletePeriod,
    deleteRun,
    transitionRun,
    fetchRun,
    downloadPayslip,
    downloadCombinedPayslips,
    downloadDraftPayslips,
    downloadPayslipsZip,
    downloadFinalSchedule,
    emailPayslip,
    emailAllPayslips,
    downloadPaymentExport,
    previewPaymentExport,
    recordPayment,
    markPayrollPaid,
    reversePayroll,
    previewAccounting,
    postAccounting,
    fetchAccountingJournals,
  };
};

export default usePayrollRuns;
